import type { CameraState, NumericRange, ProjectedPoint, Size } from '../types';
import { createProjector, getBackCorner } from '../geometry/projection';
import { AXIS_INK_TONE, GRID_INK_TONE, GRID_WIDTH } from './gridStroke';
import {
  toAxisTicks,
  type AxisPadding,
  type AxisTick,
} from '../data/horizontalAxes';
import type { AxisLabels } from '../data/axisLabels';

export type AxisChrome = {
  xTitle: string;
  yTitle: string;
  zTitle: string;
  xRange: NumericRange;
  yRange: NumericRange;
  zRange: NumericRange;
  axisLabels: AxisLabels;
};

/**
 * Which half of the chrome to draw.
 *
 * The base plane belongs under the data, but the labels do not: the vertical axis stands at
 * the corner furthest from the eye, which is exactly where a tall surface rises in front of
 * it, so labels drawn in the same pass as the plane end up behind the mesh. Splitting the
 * passes keeps the plane recessive and the labels readable.
 */
export type Axes3dPass =
  | 'plane'
  /** The vertical axis's tick labels and its title, at the back corner. */
  | 'heightLabels'
  /** Every other label: the two horizontal axes' ticks and titles. */
  | 'labels';

export type Axes3dRenderArgs = AxisChrome & {
  context: CanvasRenderingContext2D;
  camera: CameraState;
  viewport: Size;
  /** Tick labels and axis titles. */
  inkColor: string;
  /** Haloes the labels, so those crossing the surface stay readable. */
  surfaceColor: string;
  /**
   * Divisions of each axis, chosen for that axis' own range.
   *
   * One per axis rather than one for the plot: the count that suits an axis is a fact about its
   * range, so three ranges give three answers and x wanting five divisions while y wants four is
   * the ordinary case. Sharing a count leaves whichever axis did not choose it labelled off its
   * own gridlines, which reads as a rendering fault rather than as a choice about numbers.
   */
  xTickCount: number;
  yTickCount: number;
  zTickCount: number;
  /** How far each horizontal range runs past its first and last tick, in field units. */
  xPadding: AxisPadding;
  yPadding: AxisPadding;
  /**
   * How far below the plane the axis box hangs, in unit-cube z, or 0 when there is no box.
   *
   * The horizontal tick labels hang off the plane's front edge, which is also where the plinth
   * hangs from - so without this they sit on top of a slab rather than below the box.
   */
  boxDepth: number;
  /**
   * Whether the axis box is drawn behind this.
   *
   * The vertical axis at the far corner is the box's edge as well as this renderer's axis: the
   * inner face of each wall slab yields exactly `[backX, backY, 0] → [backX, backY, 1]`, and both
   * project through the same projector, so the two coincide to the pixel. `renderAxisBox` strokes
   * it in its `planes` pass at 0.45 of the ink and this used to stroke it again at the same tone
   * on the same context, compositing to `1 - 0.55² = 0.70` and making the one corner half again
   * darker than every other edge on the box.
   *
   * Stated rather than inferred from `boxDepth === 0`. That happens to hold today, but it is a
   * measurement standing in for a predicate, and a plinth without walls would break it silently.
   */
  hasAxisBox: boolean;
  pass: Axes3dPass;
};

const TICK_LABEL_FONT = '11px system-ui, -apple-system, "Segoe UI", sans-serif';
const TITLE_FONT = '600 12px system-ui, -apple-system, "Segoe UI", sans-serif';

/** An 11px label's glyph box, descenders included, rather than its em size. */
const TICK_LABEL_LINE_HEIGHT = 14;

/** The gap two neighbouring tick labels need before they read as two numbers. */
const TICK_LABEL_GAP = 4;

type PlacedLabel = { text: string; x: number; y: number; width: number };

const overlaps = (a: PlacedLabel, b: PlacedLabel): boolean =>
  Math.abs(a.x - b.x) < (a.width + b.width) / 2 + TICK_LABEL_GAP &&
  Math.abs(a.y - b.y) < TICK_LABEL_LINE_HEIGHT;

/**
 * The labels that fit along an edge, both ends always among them.
 *
 * Along a horizontal edge only shallow pitches need this. The vertical axis needs it whenever it is
 * foreshortened - looking down from above, or at a low relief - and the labels there share one
 * right-aligned anchor, so it is their vertical spacing that decides. Seen from above the edge runs diagonally and consecutive
 * labels clear each other vertically - 46px apart at the default camera - but flattened towards
 * eye level that separation collapses to around 11px while the horizontal gap closes to 36px,
 * and `niceAxis` can answer six divisions, so seven labels land on one near-horizontal line.
 *
 * The ends are kept because they are the axis's range; an interior label is kept only where it
 * clears both the last one kept and the far end.
 */
const fittingLabels = (placed: PlacedLabel[]): PlacedLabel[] => {
  if (placed.length < 3) return placed;

  const last = placed[placed.length - 1];
  const kept = [placed[0]];
  for (const candidate of placed.slice(1, -1)) {
    if (overlaps(candidate, kept[kept.length - 1])) continue;
    if (overlaps(candidate, last)) continue;
    kept.push(candidate);
  }
  return [...kept, last];
};

type ScreenPoint = { x: number; y: number };

export type EdgeTitlePlacement = ScreenPoint & {
  /** Screen y gained per pixel along the baseline. */
  slope: number;
  align: CanvasTextAlign;
};

/** The 12px title's glyph box. */
const TITLE_LINE_HEIGHT = 16;

/** Between the nearest tick number on an edge and its title. */
const TITLE_GAP = 4;

const MAX_TITLE_SLOPE = Math.tan((35 * Math.PI) / 180);

const toScreen = ({ screenX, screenY }: ProjectedPoint): ScreenPoint => ({
  x: screenX,
  y: screenY,
});

const clamp = (value: number, limit: number): number =>
  Math.max(-limit, Math.min(limit, value));

type EdgeTitleArgs = {
  from: ScreenPoint;
  to: ScreenPoint;
  middle: ScreenPoint;
  /** Any point on the floor's side of the edge. */
  inside: ScreenPoint;
  titleWidth: number;
  ticks: PlacedLabel[];
};

/** Above or below the middle of a shallow edge, letters upright and the baseline following the edge. */
const hangEdgeTitle = (
  { middle, inside, titleWidth, ticks }: EdgeTitleArgs,
  edgeSlope: number
): EdgeTitlePlacement => {
  const slope = clamp(edgeSlope, MAX_TITLE_SLOPE);
  const direction = middle.y >= inside.y ? 1 : -1;
  const lineYAt = (x: number): number => middle.y + slope * (x - middle.x);
  const titleLeft = middle.x - titleWidth / 2;
  const titleRight = middle.x + titleWidth / 2;

  const tickReaches = ticks.flatMap(({ x, y, width }) => {
    const left = Math.max(x - width / 2, titleLeft);
    const right = Math.min(x + width / 2, titleRight);
    if (left > right) return [];
    const near = direction > 0 ? y + TICK_LABEL_LINE_HEIGHT : y;
    return [left, right].map(column => direction * (near - lineYAt(column)));
  });
  const edgeReach = (Math.abs(edgeSlope - slope) * titleWidth) / 2;
  const offset =
    Math.max(edgeReach, ...tickReaches) + TITLE_GAP + TITLE_LINE_HEIGHT / 2;

  return {
    x: middle.x,
    y: middle.y + direction * offset,
    slope,
    align: 'center',
  };
};

/** Level with the middle of a steep edge, on the outside of it and its tick numbers. */
const besideEdgeTitle = (
  { middle, inside, ticks }: EdgeTitleArgs,
  edgeSlope: number
): EdgeTitlePlacement => {
  const direction = middle.x >= inside.x ? 1 : -1;
  const top = middle.y - TITLE_LINE_HEIGHT / 2;
  const bottom = middle.y + TITLE_LINE_HEIGHT / 2;
  const edgeReach = TITLE_LINE_HEIGHT / 2 / Math.abs(edgeSlope);
  const tickReaches = ticks
    .filter(({ y }) => y < bottom && y + TICK_LABEL_LINE_HEIGHT > top)
    .map(({ x, width }) => direction * (x - middle.x) + width / 2);
  const offset = Math.max(edgeReach, ...tickReaches) + TITLE_GAP;

  return {
    x: middle.x + direction * offset,
    y: middle.y,
    slope: 0,
    align: direction > 0 ? 'left' : 'right',
  };
};

/**
 * A floor axis title on the outside of its edge, clear of the tick numbers hung from it: hung
 * from the middle of an edge closer to horizontal, and beside the middle of one closer to vertical.
 */
export const placeEdgeTitle = (args: EdgeTitleArgs): EdgeTitlePlacement => {
  const { from, to } = args;
  const edgeSlope = (to.y - from.y) / (to.x - from.x);
  return Math.abs(edgeSlope) <= 1
    ? hangEdgeTitle(args, edgeSlope)
    : besideEdgeTitle(args, edgeSlope);
};

const ticksFor = ([min, max]: NumericRange, count: number): number[] =>
  Array.from(
    { length: count + 1 },
    (_, index) => min + (index / count) * (max - min)
  );

export const renderAxes3d = ({
  context,
  camera,
  viewport,
  xTitle,
  yTitle,
  zTitle,
  xRange,
  yRange,
  zRange,
  axisLabels,
  inkColor,
  surfaceColor,
  xTickCount,
  yTickCount,
  zTickCount,
  xPadding,
  yPadding,
  boxDepth,
  hasAxisBox,
  pass,
}: Axes3dRenderArgs): void => {
  const project = createProjector(camera, viewport);
  const xTicks = toAxisTicks({
    range: xRange,
    tickCount: xTickCount,
    padding: xPadding,
  });
  const yTicks = toAxisTicks({
    range: yRange,
    tickCount: yTickCount,
    padding: yPadding,
  });
  const [backX, backY] = getBackCorner(camera.yaw);

  context.save();
  context.lineJoin = 'round';

  if (pass === 'plane') {
    /* Base plane grid: the only cue for position in the plane once a surface hides the axes,
       and drawn under everything so it never competes with the data. */
    context.strokeStyle = inkColor;
    context.lineWidth = GRID_WIDTH;
    context.globalAlpha = GRID_INK_TONE;
    /* Interior rules only when there is a box. The four at `t = ±1` are its edges too - the two
       walls' inner-face base creases, and the floor's front edges, which `plinthRim` carries above
       the plane - all stroked at `EDGE_ALPHA`. Ruling them again composites 0.45 with the grid tone
       to 0.56 and makes the bottom of the box darker than the mitre and the wall tops. `axisBoxGrid`
       states the same rule for the walls and applies it through `interiorTicks`.

       Boxless, they are the only thing drawing the plane's boundary, so they stay. */
    /* One loop per direction, because the two axes are ruled at their own counts. Sharing the
       loop tied a rule crossing the plane to a division of the axis it runs parallel to rather
       than of the one it crosses, so an x wanting five divisions and a y wanting four drew both
       families at five and left the y labels between its own rules. */
    context.beginPath();
    for (const [ticks, isAlongX] of [
      [xTicks, true],
      [yTicks, false],
    ] as const) {
      for (const { fraction } of ticks) {
        const t = fraction * 2 - 1;
        if (hasAxisBox && Math.abs(t) === 1) continue;
        /* A rule at a division of x runs parallel to y, and the other way round. */
        const [from, to] = isAlongX
          ? [project(t, -1, 0), project(t, 1, 0)]
          : [project(-1, t, 0), project(1, t, 0)];
        context.moveTo(from.screenX, from.screenY);
        context.lineTo(to.screenX, to.screenY);
      }
    }
    context.stroke();

    /* The vertical axis, standing at the far corner - only when nothing else draws it. The box owns
       that segment when there is one, as its walls' shared inner edge, and strokes it at the same
       0.45; drawing it twice on one context composites to 0.70. Its own tone, stated rather than
       inherited: it shared a `strokeStyle` with the grid, so in the grid's grey at full strength it
       was the fainter of the two the moment the grid moved to ink. */
    if (!hasAxisBox) {
      context.strokeStyle = inkColor;
      context.globalAlpha = AXIS_INK_TONE;
      context.lineWidth = 1;
      const zBottom = project(backX, backY, 0);
      const zTop = project(backX, backY, 1);
      context.beginPath();
      context.moveTo(zBottom.screenX, zBottom.screenY);
      context.lineTo(zTop.screenX, zTop.screenY);
      context.stroke();
    }

    context.restore();
    return;
  }

  context.fillStyle = inkColor;
  context.strokeStyle = surfaceColor;
  context.lineWidth = 3;
  context.lineJoin = 'round';
  context.font = TICK_LABEL_FONT;

  /* Each label is knocked out of whatever is behind it before being inked, so a tick
     crossing the mesh stays legible without an opaque box hiding the data around it. */
  const label = (text: string, x: number, y: number): void => {
    context.strokeText(text, x, y);
    context.fillText(text, x, y);
  };

  if (pass === 'heightLabels') {
    context.textAlign = 'right';
    context.textBaseline = 'middle';

    const [zMin, zMax] = zRange;
    const zSpan = zMax - zMin || 1;
    const placed = ticksFor(zRange, zTickCount).map(value => {
      const { screenX, screenY } = project(
        backX,
        backY,
        (value - zMin) / zSpan
      );
      const text = axisLabels.z.value(value);
      return {
        text,
        x: screenX - 8,
        y: screenY,
        width: context.measureText(text).width,
      };
    });
    fittingLabels(placed).forEach(({ text, x, y }) => label(text, x, y));

    context.font = TITLE_FONT;
    context.textAlign = 'right';
    context.textBaseline = 'bottom';
    const zTitleAt = project(backX, backY, 1);
    label(zTitle, zTitleAt.screenX - 8, zTitleAt.screenY - 10);

    context.restore();
    return;
  }

  /* Horizontal tick labels, along the two edges that meet the far corner. */
  context.textAlign = 'center';
  context.textBaseline = 'top';
  /* Hung off the bottom of the box rather than off the plane, when there is a box. The plane's
     front edge is where the plinth hangs from, and the slab is 13 pixels deep at the default camera
     and deeper the flatter it gets - so anchoring here put its bottom edge through the numbers, and
     put each number's `surfaceColor` halo into the panel as a page-coloured notch. `boxDepth` is 0
     when the box is off, which is the behaviour these two loops had before it existed. */
  const placeAlong = (
    ticks: AxisTick[],
    toCube: (t: number) => { x: number; y: number },
    formatTick: (value: number) => string
  ): PlacedLabel[] =>
    ticks.map(({ value, fraction }) => {
      const { x, y } = toCube(fraction * 2 - 1);
      const { screenX, screenY } = project(x, y, boxDepth);
      const text = formatTick(value);
      return {
        text,
        x: screenX,
        y: screenY + 8,
        width: context.measureText(text).width,
      };
    });

  const edges = [
    {
      title: xTitle,
      ticks: fittingLabels(
        placeAlong(xTicks, t => ({ x: t, y: -backY }), axisLabels.x.value)
      ),
      from: project(-1, -backY, boxDepth),
      to: project(1, -backY, boxDepth),
      middle: project(0, -backY, boxDepth),
    },
    {
      title: yTitle,
      ticks: fittingLabels(
        placeAlong(yTicks, t => ({ x: -backX, y: t }), axisLabels.y.value)
      ),
      from: project(-backX, -1, boxDepth),
      to: project(-backX, 1, boxDepth),
      middle: project(-backX, 0, boxDepth),
    },
  ];

  for (const { ticks } of edges) {
    for (const { text, x, y } of ticks) label(text, x, y);
  }

  const floorCenter = toScreen(project(0, 0, boxDepth));
  context.font = TITLE_FONT;
  context.textBaseline = 'middle';
  for (const { title, ticks, from, to, middle } of edges) {
    const { x, y, slope, align } = placeEdgeTitle({
      from: toScreen(from),
      to: toScreen(to),
      middle: toScreen(middle),
      inside: floorCenter,
      titleWidth: context.measureText(title).width,
      ticks,
    });
    context.save();
    context.textAlign = align;
    context.translate(x, y);
    context.transform(1, slope, 0, 1, 0, 0);
    label(title, 0, 0);
    context.restore();
  }

  context.restore();
};
